import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import type { PersonalCustomEmoji, PersonalCustomEmojiSet } from "../../api/client";
import { telegramWebApp } from "../../telegram/webapp";
import { CustomEmoji } from "./CustomEmoji";

type Props = {
  sets: PersonalCustomEmojiSet[];
  recent: PersonalCustomEmoji[];
  onSelect: (emoji: PersonalCustomEmoji) => void;
  onClose: () => void;
  onImport?: () => void;
  targetFallback?: string;
  loadPreview?: (customEmojiId: string, signal?: AbortSignal) => Promise<Blob>;
};

const importLink = "https://t.me/THLS_AllPrice_bot?start=emoji_import";

export function CustomEmojiPicker({
  sets,
  recent,
  onSelect,
  onClose,
  onImport,
  targetFallback,
  loadPreview
}: Props) {
  const [active, setActive] = useState(recent.length ? "recent" : sets[0]?.id ?? "");
  const [query, setQuery] = useState("");
  const dialogRef = useRef<HTMLElement | null>(null);
  const normalizedQuery = query.trim().toLocaleLowerCase("ru");

  useEffect(() => {
    telegramWebApp()?.disableVerticalSwipes?.();
    dialogRef.current?.focus();
    return () => telegramWebApp()?.enableVerticalSwipes?.();
  }, []);

  useEffect(() => {
    const activeStillExists = active === "recent"
      ? recent.length > 0
      : sets.some((set) => set.id === active);
    if (!activeStillExists) setActive(recent.length ? "recent" : sets[0]?.id ?? "");
  }, [active, recent.length, sets]);

  const items = useMemo(() => {
    const source = normalizedQuery
      ? sets.flatMap((set) => set.emoji.map((emoji) => ({ emoji, setTitle: set.title })))
      : active === "recent"
        ? recent.map((emoji) => ({ emoji, setTitle: emoji.setName ?? "" }))
        : (sets.find((set) => set.id === active)?.emoji ?? []).map((emoji) => ({
            emoji,
            setTitle: sets.find((set) => set.id === active)?.title ?? ""
          }));
    return source.filter(({ emoji, setTitle }) => !normalizedQuery ||
      emoji.fallback.toLocaleLowerCase("ru").includes(normalizedQuery) ||
      setTitle.toLocaleLowerCase("ru").includes(normalizedQuery));
  }, [active, normalizedQuery, recent, sets]);

  const matchingEmoji = useMemo(() => {
    if (!targetFallback) return [];
    const seen = new Set<string>();
    return sets.flatMap((set) => set.emoji).filter((emoji) => {
      if (emoji.fallback !== targetFallback || seen.has(emoji.customEmojiId)) return false;
      seen.add(emoji.customEmojiId);
      return true;
    });
  }, [sets, targetFallback]);

  const beginImport = () => {
    if (onImport) onImport();
    else telegramWebApp()?.openTelegramLink?.(importLink) ?? window.open(importLink, "_blank", "noopener,noreferrer");
  };

  const sheet = (
    <div className="custom-emoji-backdrop" onPointerDown={(event) => { if (event.currentTarget === event.target) onClose(); }}>
      <section
        ref={dialogRef}
        className="custom-emoji-picker"
        role="dialog"
        aria-modal="true"
        aria-label="Выбор премиум эмодзи"
        tabIndex={-1}
        onKeyDown={(event) => { if (event.key === "Escape") onClose(); }}
      >
        <header className="custom-emoji-picker-header">
          <div><p className="eyebrow">Telegram Premium</p><h2>Эмодзи</h2></div>
          <button type="button" aria-label="Закрыть выбор эмодзи" onClick={onClose}>×</button>
        </header>
        <div className="custom-emoji-picker-sticky">
          <label className="search-field"><span aria-hidden="true">⌕</span><input type="search" aria-label="Поиск эмодзи" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Эмодзи или название набора" /></label>
          {sets.length > 0 && <div className="custom-emoji-tabs" role="tablist" aria-label="Наборы эмодзи">
            {recent.length > 0 && <button type="button" role="tab" aria-selected={active === "recent"} onClick={() => { setActive("recent"); setQuery(""); }}>Недавние</button>}
            {sets.map((set) => <button type="button" role="tab" aria-selected={active === set.id} key={set.id} onClick={() => { setActive(set.id); setQuery(""); }}>{set.title}</button>)}
          </div>}
        </div>
        <div className="custom-emoji-scroll" data-testid="custom-emoji-scroll">
          {targetFallback && matchingEmoji.length > 0
            ? <section className="custom-emoji-matches" data-testid="custom-emoji-matches">
                <h3>Для {targetFallback}</h3>
                <div className="custom-emoji-grid">{matchingEmoji.map((emoji) => <button type="button" aria-label={emoji.fallback} title={emoji.fallback} key={emoji.customEmojiId} onClick={() => onSelect(emoji)}><CustomEmoji emoji={emoji} loadPreview={loadPreview} /></button>)}</div>
              </section>
            : targetFallback
              ? <p className="custom-emoji-replacement-helper">Выберите Premium Emoji для замены {targetFallback}</p>
              : null}
          {sets.length === 0 ? <div className="custom-emoji-empty"><strong>Добавьте свой набор</strong><p>Отправьте боту один Premium-эмодзи или ссылку на набор — он появится здесь.</p><button className="primary-button" type="button" onClick={beginImport}>Добавить набор</button></div>
            : items.length === 0 ? <p className="custom-emoji-no-results">Ничего не найдено</p>
              : <div className="custom-emoji-grid">{items.map(({ emoji }) => <button type="button" aria-label={emoji.fallback} title={emoji.fallback} key={emoji.customEmojiId} onClick={() => onSelect(emoji)}><CustomEmoji emoji={emoji} loadPreview={loadPreview} /></button>)}</div>}
          {sets.length > 0 && <button className="custom-emoji-import" type="button" onClick={beginImport}>+ Добавить набор</button>}
        </div>
      </section>
    </div>
  );
  return typeof document === "undefined" ? sheet : createPortal(sheet, document.body);
}
